import { EditorOnly } from '../components/EditorOnly';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  MenuItem,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useEffect, useState, type FormEvent } from 'react';
import { Link as RouterLink } from 'react-router';
import { api } from '../api/client';
import type { TelegramOwnedChannel } from '../api/types';
import { Stack, Typography } from '../components/mui-system';

const message = (error: unknown) =>
  error instanceof Error ? error.message : 'Не удалось выполнить действие';
const date = (value: string) => new Date(value).toLocaleString('ru-RU');

export function RegistryTelegramChannels({
  workspaceId,
  channelId,
  onClearChannel,
}: {
  workspaceId: string;
  channelId?: string | null;
  onClearChannel?: () => void;
}) {
  const [rows, setRows] = useState<TelegramOwnedChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('owned');
  const [editing, setEditing] = useState<TelegramOwnedChannel | 'new' | null>(null);
  const [owned, setOwned] = useState(true);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<TelegramOwnedChannel[] | null>(null);
  const editingRow = editing && editing !== 'new' ? editing : null;
  useEffect(() => {
    let current = true;
    api
      .telegramRegistry(workspaceId)
      .then((data) => {
        if (current) setRows(data);
      })
      .catch((caught) => {
        if (current) setError(message(caught));
      })
      .finally(() => {
        if (current) setLoading(false);
      });
    return () => {
      current = false;
    };
  }, [workspaceId]);

  async function action(run: () => Promise<void>) {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await run();
    } catch (caught) {
      setError(message(caught));
    } finally {
      setBusy(false);
    }
  }
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    await action(async () => {
      const title = String(data.get('title'));
      const notes = String(data.get('notes'));
      const row = editingRow
        ? await api.updateTelegramRegistryEntry(workspaceId, editingRow.id, {
            title,
            notes,
            owned,
            revision: editingRow.revision,
          })
        : await api.createTelegramRegistryEntry(workspaceId, {
            title,
            notes,
            username: String(data.get('username')),
          });
      setRows((current) =>
        [...current.filter((item) => item.id !== row.id), row].sort((a, b) =>
          a.username.localeCompare(b.username),
        ),
      );
      setEditing(null);
      setNotice(
        'Реестр сохранён. «Карта выдачи» учтёт изменения при следующем открытии результатов.',
      );
    });
  }
  const visible = rows.filter((row) =>
    channelId
      ? row.id === channelId
      : (filter === 'all' || (filter === 'owned' ? row.owned : !row.owned)) &&
        `${row.title} ${row.username} ${row.notes}`.toLowerCase().includes(search.toLowerCase()),
  );
  return (
    <Stack gap={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}
      <Card>
        <CardContent>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            gap={2}
            justifyContent="space-between"
            alignItems={{ md: 'center' }}
          >
            <Stack gap={0.5}>
              <Typography variant="h3">Реестр наших каналов</Typography>
              <Typography color="text.secondary">
                {rows.filter((row) => row.owned).length} наших ·{' '}
                {rows.filter((row) => !row.owned).length} больше не наших
              </Typography>
            </Stack>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <EditorOnly permissions="registry.create">
                <Button
                  variant="contained"
                  disabled={busy || loading}
                  onClick={() => {
                    setEditing('new');
                    setOwned(true);
                    setError('');
                  }}
                >
                  Добавить канал
                </Button>
              </EditorOnly>
              <Button
                disabled={busy || loading}
                onClick={() =>
                  void action(async () => {
                    setRows(await api.telegramRegistry(workspaceId));
                  })
                }
              >
                Обновить
              </Button>
            </Stack>
          </Stack>
          <Typography mt={2} color="text.secondary">
            Каналы из этого списка отмечаются как наши в разделе «Карта выдачи». Токен бота не
            нужен. Отсутствие в реестре требует проверки и само по себе не означает фейк.
          </Typography>
          <Link
            component={RouterLink}
            to="/docs/registry-telegram"
            sx={{ display: 'inline-block', mt: 1 }}
          >
            Как вести реестр каналов
          </Link>
        </CardContent>
      </Card>
      {channelId && (
        <Alert
          severity={!loading && !error && !visible.length ? 'error' : 'info'}
          action={<Button onClick={onClearChannel}>Все каналы</Button>}
        >
          {!loading && !error && !visible.length
            ? 'Канал из ссылки не найден в текущем рабочем пространстве.'
            : 'Канал, выбранный в Реестре ресурсов.'}
        </Alert>
      )}
      {!channelId && (
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
          <TextField
            size="small"
            label="Поиск по каналам"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            fullWidth
          />
          <TextField
            select
            size="small"
            label="Принадлежность"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            sx={{ minWidth: 210 }}
          >
            <MenuItem value="owned">Наши</MenuItem>
            <MenuItem value="released">Больше не наши</MenuItem>
            <MenuItem value="all">Все записи</MenuItem>
          </TextField>
        </Stack>
      )}
      {loading ? (
        <CircularProgress aria-label="Загрузка реестра" />
      ) : !visible.length ? (
        !error &&
        !channelId && (
          <Alert severity="info">
            {rows.length
              ? 'По выбранным условиям каналов нет.'
              : 'Реестр пуст. Добавьте наши каналы.'}
          </Alert>
        )
      ) : (
        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Канал</TableCell>
                  <TableCell>Принадлежность</TableCell>
                  <TableCell>Основание / заметка</TableCell>
                  <TableCell>Обновлён</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Link href={`https://t.me/${row.username}`} target="_blank" rel="noreferrer">
                        @{row.username}
                      </Link>
                      <Typography fontSize={13}>{row.title}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={row.owned ? 'success' : 'default'}
                        label={row.owned ? 'Наш канал' : 'Больше не наш'}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320, overflowWrap: 'anywhere' }}>
                      {row.notes || '—'}
                    </TableCell>
                    <TableCell>{date(row.updatedAt)}</TableCell>
                    <TableCell>
                      <Stack direction="row">
                        <EditorOnly permissions="registry.edit">
                          <Button
                            disabled={busy}
                            onClick={() => {
                              setEditing(row);
                              setOwned(row.owned);
                              setError('');
                            }}
                          >
                            Изменить
                          </Button>
                        </EditorOnly>
                        <Button
                          disabled={busy}
                          onClick={() =>
                            void action(async () =>
                              setHistory(await api.telegramRegistryHistory(workspaceId, row.id)),
                            )
                          }
                        >
                          История
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}
      <Dialog
        open={editing !== null}
        onClose={() => {
          if (!busy) setEditing(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <form onSubmit={(event) => void save(event)}>
          <DialogTitle>
            {editingRow ? `Канал @${editingRow.username}` : 'Добавить наш канал'}
          </DialogTitle>
          <DialogContent>
            <Stack gap={2} mt={1}>
              {error && <Alert severity="error">{error}</Alert>}
              {!editingRow && (
                <TextField
                  name="username"
                  label="Username или ссылка на канал"
                  placeholder="@our_channel или https://t.me/our_channel"
                  required
                  fullWidth
                />
              )}
              <TextField
                name="title"
                label="Название"
                defaultValue={editingRow?.title ?? ''}
                required
                fullWidth
              />
              <TextField
                name="notes"
                label={editingRow ? 'Основание изменения' : 'Основание / заметка'}
                required={Boolean(editingRow)}
                multiline
                minRows={2}
                fullWidth
              />
              {editingRow && (
                <FormControlLabel
                  control={<Switch checked={owned} onChange={(_, checked) => setOwned(checked)} />}
                  label="Канал принадлежит нам"
                />
              )}
              {editingRow && !owned && (
                <Alert severity="warning">
                  Канал перестанет определяться как наш. История сохранится.
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={busy} onClick={() => setEditing(null)}>
              Отмена
            </Button>
            <Button type="submit" variant="contained" disabled={busy}>
              Сохранить
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <Dialog open={history !== null} onClose={() => setHistory(null)} fullWidth maxWidth="sm">
        <DialogTitle>История канала {history?.[0] && `@${history[0].username}`}</DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            {history?.map((row) => (
              <Card key={row.revision}>
                <CardContent>
                  <Typography fontWeight={700}>
                    {row.owned ? 'Наш канал' : 'Больше не наш'} · {date(row.updatedAt)}
                  </Typography>
                  <Typography>{row.title}</Typography>
                  <Typography>{row.notes || 'Добавлен вручную'}</Typography>
                  <Typography color="text.secondary" fontSize={12}>
                    Версия {row.revision} · Автор: {row.updatedBy}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistory(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
